import { useState, useCallback } from 'react';
import SEO from '../../components/SEO';
import AdUnit from '../../components/AdUnit';

function hexToRgb(hex) {
  var h = hex.replace('#', '');
  if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
  var n = parseInt(h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(function (x) { var s = x.toString(16); return s.length === 1 ? '0' + s : s; }).join('');
}

function rgbToHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  var max = Math.max(r, g, b), min = Math.min(r, g, b);
  var h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    var d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return { h: Math.round(h * 360), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function ColorConverter() {
  const [hex, setHex] = useState('#6c63ff');
  const [rgb, setRgb] = useState({ r: 108, g: 99, b: 255 });
  const [hsl, setHsl] = useState({ h: 243, s: 100, l: 69 });
  const [copied, setCopied] = useState('');

  var updateFromHex = useCallback(function (val) {
    setHex(val);
    var clean = val.replace('#', '');
    if (clean.length === 3 || clean.length === 6) {
      var c = hexToRgb(val);
      setRgb(c);
      setHsl(rgbToHsl(c.r, c.g, c.b));
    }
  }, []);

  var updateFromRgb = useCallback(function (key, val) {
    var n = Math.max(0, Math.min(255, parseInt(val) || 0));
    var next = Object.assign({}, rgb, { [key]: n });
    setRgb(next);
    setHex(rgbToHex(next.r, next.g, next.b));
    setHsl(rgbToHsl(next.r, next.g, next.b));
  }, [rgb]);

  var copyText = function (text, label) {
    navigator.clipboard.writeText(text).then(function () { setCopied(label); setTimeout(function () { setCopied(''); }, 2000); });
  };

  var hexStr = hex;
  var rgbStr = 'rgb(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b + ')';
  var hslStr = 'hsl(' + hsl.h + ', ' + hsl.s + '%, ' + hsl.l + '%)';

  return (
    <div className="page-prettifier">
      <SEO title="Color Converter - HEX, RGB, HSL" description="Free online color converter. Convert between HEX, RGB, and HSL color formats instantly with a live preview." path="/color-converter" />
      <div className="container">
        <div className="tool-header">
          <h1>Color Converter</h1>
          <p>Convert colors between HEX, RGB, and HSL formats with a live preview.</p>
        </div>
        <AdUnit />
        <div className="color-tool">
          <div className="color-preview" style={{ background: hexStr }}></div>
          <div className="color-inputs">
            <div className="color-row">
              <label>HEX</label>
              <input type="text" value={hexStr} onChange={function (e) { updateFromHex(e.target.value); }} className="color-input" />
              <button onClick={function () { copyText(hexStr, 'hex'); }} className="copy-btn" type="button">{copied === 'hex' ? '✓' : 'Copy'}</button>
            </div>
            <div className="color-row">
              <label>RGB</label>
              <div className="color-rgb-inputs">
                <input type="number" min="0" max="255" value={rgb.r} onChange={function (e) { updateFromRgb('r', e.target.value); }} className="color-input-sm" aria-label="Red" />
                <input type="number" min="0" max="255" value={rgb.g} onChange={function (e) { updateFromRgb('g', e.target.value); }} className="color-input-sm" aria-label="Green" />
                <input type="number" min="0" max="255" value={rgb.b} onChange={function (e) { updateFromRgb('b', e.target.value); }} className="color-input-sm" aria-label="Blue" />
              </div>
              <button onClick={function () { copyText(rgbStr, 'rgb'); }} className="copy-btn" type="button">{copied === 'rgb' ? '✓' : 'Copy'}</button>
            </div>
            <div className="color-row">
              <label>HSL</label>
              <input type="text" value={hslStr} readOnly className="color-input" />
              <button onClick={function () { copyText(hslStr, 'hsl'); }} className="copy-btn" type="button">{copied === 'hsl' ? '✓' : 'Copy'}</button>
            </div>
          </div>
        </div>
        <section className="tool-guide">
          <h2>When Do You Need Color Conversion?</h2>
          <p>Designers and developers constantly switch between color formats. CSS uses HEX and RGB, design tools often show HSL, and brand guidelines might specify any format. This tool lets you convert between them instantly and copy the exact value you need.</p>
        </section>
      </div>
    </div>
  );
}

export default ColorConverter;
